import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Most-recent search queries the player has run, newest first. Backs the
 * "Recent Searches" section on the search page (ARC-113 follow-up). The list is
 * durable (localStorage) so it survives a refresh, capped at
 * {@link MAX_RECENT_SEARCHES}, and de-duplicated case-insensitively — re-running
 * an old query just moves it back to the top rather than adding a twin row.
 *
 * Only the user's own typed queries live here; "Popular" rows are server data
 * (the lobby feed), not history, so they never touch this store.
 */
export const MAX_RECENT_SEARCHES = 8;

interface SearchHistoryState {
  recent: string[];
  record: (query: string) => void;
  clear: () => void;
}

const useSearchHistoryStore = create<SearchHistoryState>()(
  persist(
    (set) => ({
      recent: [],
      record: (query) => {
        const trimmed = query.trim();
        if (!trimmed) return;
        set((state) => {
          const withoutDupe = state.recent.filter((q) => q.toLowerCase() !== trimmed.toLowerCase());
          return { recent: [trimmed, ...withoutDupe].slice(0, MAX_RECENT_SEARCHES) };
        });
      },
      clear: () => set({ recent: [] }),
    }),
    { name: 'predictions.search-history' },
  ),
);

/** The player's recent search queries, newest first (survives refresh). */
export const useRecentSearches = (): string[] => useSearchHistoryStore((s) => s.recent);

/** Record a query at the top of the recent list. No-op for blank input; idempotent per query. */
export const useRecordSearch = (): ((query: string) => void) => useSearchHistoryStore((s) => s.record);

/** Clear the entire recent-search list (the "Clear All" action). */
export const useClearRecentSearches = (): (() => void) => useSearchHistoryStore((s) => s.clear);

/** Imperative clear for non-React call sites (e.g. logout). */
export const clearRecentSearchesImperative = (): void => useSearchHistoryStore.getState().clear();
